import { useEffect, useState, type ReactNode } from 'react';
import { adminApi } from '@/lib/adminApi';
import {
  isAdminAuthenticated,
  onAdminAuthChange,
  setAdminSession,
  setAdminPermissions,
} from '@/lib/adminAuth';
import { ApiError, ApiAuthError } from '@/lib/api';

/**
 * Staff-auth gate for the ADMIN section. Renders an email+password login until
 * a staff JWT is stored, then the children (the Admin pages). Unlike the ops
 * gate (a shared-secret stop-gap), this performs a REAL login against admin-svc
 * (POST /v1/admin/login) and stores the returned scope:"admin" JWT.
 *
 * On (re)mount with a stored token, it re-fetches /v1/admin/me to RESYNC the
 * role's permission set from the server (the source of truth) — so a role
 * change or a stale localStorage set can't leave the UI offering actions the
 * server now denies.
 */
export function AdminAuthGate({ children }: { children: ReactNode }) {
  const [authed, setAuthed] = useState<boolean>(() => isAdminAuthenticated());
  useEffect(() => onAdminAuthChange(setAuthed), []);

  // Resync permissions from the server whenever we (re)enter the authed state.
  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    adminApi
      .me()
      .then((res) => {
        if (!cancelled) setAdminPermissions(res.permissions ?? []);
      })
      .catch(() => {
        // A 401 here means the token is dead; the global handler / api layer
        // already bounces those. Other errors: keep the login-time permissions.
      });
    return () => {
      cancelled = true;
    };
  }, [authed]);

  return authed ? <>{children}</> : <AdminLoginScreen />;
}

function AdminLoginScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError('Enter your staff email and password.');
      return;
    }
    setBusy(true);
    try {
      const res = await adminApi.login(email.trim(), password);
      // Store the JWT + profile + the role's permissions (the gate flips, and
      // the UI gates its actions off these — same set the server enforces).
      setAdminSession(res.access_token, res.admin, res.permissions ?? []);
      // onAdminAuthChange flips the gate; nothing else to do.
    } catch (err) {
      if (err instanceof ApiAuthError || (err instanceof ApiError && err.status === 401)) {
        setError('Invalid credentials.');
      } else if (err instanceof ApiError) {
        setError(err.message || 'Login failed.');
      } else {
        setError('Could not reach admin-svc. Is it running?');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink-950 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-ink0 font-bold text-white">
            T
          </div>
          <div>
            <div className="text-lg font-semibold text-slate-100">Telride Admin</div>
            <div className="text-xs uppercase tracking-widest text-text-muted">Staff Console</div>
          </div>
        </div>

        <form onSubmit={submit} className="panel space-y-4 p-6">
          <div>
            <label htmlFor="admin-email" className="block text-[13px] font-medium text-slate-200">
              Staff email
            </label>
            <input
              id="admin-email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@telride.com.au"
              className="input mt-1.5"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="block text-[13px] font-medium text-slate-200">
              Password
            </label>
            <input
              id="admin-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="input mt-1.5"
            />
          </div>

          {error ? <p className="text-xs text-danger">{error}</p> : null}

          <button type="submit" disabled={busy} className="btn-primary w-full">
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
